import InformationCard from "../InformationCard";
import TelehealthLoading from "../TelehealthLoading";
import TelehealthMessageCard from "../TelehealthMessageCard";
import { useParams } from "react-router";
import { useTelehealthSession } from "./useTelehealthSession";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { Link, palette, Typography } from "@powerdiary/components-library/lib";
import { Lock } from "lucide-react";
import Content from "../../Layout/Content";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { useEffect } from "react";

// Final client-facing copy — kept as constants so wording changes stay a one-line edit.
const INVALID_LINK_MESSAGE = "This Telehealth link is no longer valid.";
const INVALID_LINK_SECONDARY = "Please request an updated link from your practice.";
const LOAD_ERROR_MESSAGE = "We couldn't load your session details.";
const LOAD_ERROR_SECONDARY = "Please check your connection and try again.";

const Telehealth = () => {
    const { id } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const { sessions, isLoading, isError, isInvalidLink, refetch } = useTelehealthSession(id);
    const { track } = useAnalytics();

    const renderSessionContent = () => {
        if (isLoading) {
            return <TelehealthLoading />;
        }

        if (isError) {
            return isInvalidLink ? (
                <TelehealthMessageCard
                    message={INVALID_LINK_MESSAGE}
                    secondaryMessage={INVALID_LINK_SECONDARY}
                    dataTestId="card_invalid_link"
                />
            ) : (
                <TelehealthMessageCard
                    message={LOAD_ERROR_MESSAGE}
                    secondaryMessage={LOAD_ERROR_SECONDARY}
                    onRetry={() => void refetch()}
                    dataTestId="card_load_error"
                />
            );
        }

        return <InformationCard sessions={sessions} />;
    };

    useEffect(() => {
        track("page_view", {
            page_title: "Telehealth",
            session_count: sessions?.length || 0,
            has_expired_session: sessions?.some((s) => s.isRoomExpired) || false,
        });
    }, [track, sessions]);

    return (
        <Content dataTestIdSuffix="telehealth">
            <Stack spacing={2} p={isMobile ? 2 : 4}>
                <Box display="flex" justifyContent="center" marginTop="68px" textAlign="center">
                    <Typography variant="heading2" data-test-id="text_join_telehealth" color={palette.neutral["900"]}>
                        Join a Telehealth Session
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="paragraphMediumRegular"
                        mt={1}
                        display="flex"
                        flexDirection="row"
                        gap="6px"
                        justifyContent="center"
                        alignItems="center"
                        data-test-id="text_secure_text"
                        color={palette.neutral["700"]}
                    >
                        <Lock size={14} strokeWidth={2} aria-hidden /> All sessions are secure and encrypted
                    </Typography>
                </Box>

                {renderSessionContent()}

                <Box textAlign="center" marginTop="30px">
                    <Typography
                        variant="paragraphMediumRegular"
                        data-test-id="text_powered_zoom"
                        color={palette.neutral["700"]}
                    >
                        Telehealth is powered by Zoom.{" "}
                    </Typography>
                    <Typography variant="paragraphMediumRegular" color={palette.neutral["700"]}>
                        Get the most out of Zoom by downloading the
                        <Link
                            sx={{ marginLeft: "5px", marginRight: "5px" }}
                            href="https://zoom.us/download"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-test-id="link_zoom_download_app"
                            onClick={() => track("telehealth_zoom_download_clicked")}
                        >
                            Zoom Desktop App
                        </Link>
                        before your session starts.
                    </Typography>
                </Box>
            </Stack>
        </Content>
    );
};

export default Telehealth;
